import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type * as React from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentLink } from "apis/v3/authApi";
import { MEMBERSHIP_ANNUAL_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { AGREEMENT_FORM_PRESENTATION_ORDER } from "constants/v3/formConstants";
import {
  memberHasDateOfBirth,
  userStateHasGlMemberStatus
} from "utils/v3/auth0ClaimsUtils";
import { useMembershipActivationAfterPayment } from "hooks/useMembershipActivationAfterPayment";
import { usePaymentIframeEmbedGuard } from "hooks/usePaymentIframeEmbedGuard";
import { useUnaricPaypagePostMessage } from "hooks/useUnaricPaypagePostMessage";
import { trackPaymentCompleted, trackPaymentFailed } from "analytics";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import { COLORS } from "components/v3/Theme/colors";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { selectAllFormSubmissions } from "store/reducers/formTemplates/FormTemplatesSlice";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import PageTitle from "utils/PageTitle";
import UnpaidInviteCompleteSummaryCard from "../UnpaidInviteProfilePage/UnpaidInviteCompleteSummaryCard";
import UnpaidInviteRegistrationStepper from "../UnpaidInviteProfilePage/UnpaidInviteRegistrationStepper";

const LOADING_VIEWPORT_MAX_PX = 500;
const IFRAME_MIN_HEIGHT_PX = 850;
const IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX = 200;
const IFRAME_NARROW_MAX_WIDTH_CSS = "(max-width: 526px)";
const EXPAND_MAX_PX = 1600;
const IFRAME_ROUNDED_CORNER_RADIUS_PX = 10;
const IFRAME_ROUNDED_CORNER_PADDING_PX = 10;
const IFRAME_CHROME_VERTICAL_EXTRA_PX = IFRAME_ROUNDED_CORNER_PADDING_PX * 2;

const REQUIRED_AGREEMENT_FORM_IDS = AGREEMENT_FORM_PRESENTATION_ORDER;

const UnpaidInvitePaymentPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userPersonalInformation = useSelector(selectUserPersonalInformation);
  const { dob } = userPersonalInformation;
  const allFormSubmissions = useSelector(selectAllFormSubmissions);

  // Any plan status (active or lapsed) means this member has already paid;
  // the unpaid-invite purchase flow is only for members with no status.
  const requiresPayment = !userStateHasGlMemberStatus(userPersonalInformation);

  // Payment must not be reachable (e.g. by direct URL) before the member has
  // agreed to all legal documents and completed their profile DOB.
  const onboardingPrerequisitesComplete =
    REQUIRED_AGREEMENT_FORM_IDS.every((formId) =>
      allFormSubmissions.some(
        (submission) => submission.formInfo.id === formId
      )
    ) && memberHasDateOfBirth(dob);

  const [ecommerceLink, setEcommerceLink] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);

  const isNarrowPaymentViewport = useMediaQuery(IFRAME_NARROW_MAX_WIDTH_CSS, {
    defaultMatches: false
  });
  const iframeMinHeightLoaded =
    IFRAME_MIN_HEIGHT_PX +
    (isNarrowPaymentViewport ? IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX : 0);
  const outerMinHeightLoading =
    LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMinHeightLoaded =
    iframeMinHeightLoaded + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMaxHeightLoaded = EXPAND_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;

  const {
    beginActivationWait,
    showLongWaitCopy,
    waitingForActivation
  } = useMembershipActivationAfterPayment({
    onActivated: () => {
      trackPaymentCompleted({ paymentType: "unpaid_invite" });
      history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_RECEIPT, {
        quantity: 1,
        totalUsd: MEMBERSHIP_ANNUAL_PRICE_USD
      });
    },
    onActivationTimeout: () => {
      trackPaymentFailed({
        paymentType: "unpaid_invite",
        failureReason: "payment_accepted_account_not_activated"
      });
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText:
            "Your payment was received, but we could not confirm your membership yet. Please try again in a few minutes or contact support."
        })
      );
      history.push(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
    }
  });

  const handlePaymentIframeComplete = useCallback(() => {
    beginActivationWait();
  }, [beginActivationWait]);

  const formattedInitialPayment = useMemo(() => {
    const money = new Intl.NumberFormat("en-US", {
      currency: "USD",
      style: "currency"
    }).format(MEMBERSHIP_ANNUAL_PRICE_USD);
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `Initial payment of ${money} from ${y}-${m}-${d}`;
  }, []);

  useEffect(() => {
    setIframeLoaded(false);
  }, [ecommerceLink]);

  const handlePaymentEmbedFailed = useCallback(() => {
    dispatch(
      openGLSnackbar({
        variant: "error",
        header: "",
        subText:
          "The payment form couldn’t be loaded. Please try again and reach out to support if the problem persists."
      })
    );
    history.push(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
  }, [dispatch, history]);

  usePaymentIframeEmbedGuard({
    ecommerceLink,
    iframeLoaded,
    enabled: Boolean(ecommerceLink) && !waitingForActivation,
    onEmbedFailed: handlePaymentEmbedFailed
  });

  const handleLoadFailure = useCallback(() => {
    dispatch(
      openGLSnackbar({
        variant: "error",
        header: "",
        subText: "Something went wrong, please try again."
      })
    );
    history.push(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
  }, [dispatch, history]);

  useUnaricPaypagePostMessage({
    iframeSrc: ecommerceLink,
    enabled: Boolean(ecommerceLink) && !waitingForActivation,
    paymentIframeRef,
    handlers: {
      onSuccessScreen: handlePaymentIframeComplete,
      onComplete: handlePaymentIframeComplete,
      onCancel: () => {
        dispatch(
          openGLSnackbar({
            variant: "info",
            header: "",
            subText: "Payment was cancelled."
          })
        );
        history.push(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
      },
      onErrorWithMessage: (message) => {
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText:
              message ||
              "We couldn’t process your payment. You can try again or contact support."
          })
        );
      },
      onErrorScreen: () => {
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText: "There was a problem with your payment."
          })
        );
      },
      onDirectDebitDialog: () => {
        globalThis.scrollTo(0, 0);
      }
    }
  });

  useEffect(() => {
    if (waitingForActivation) {
      return;
    }
    if (!requiresPayment) {
      history.replace(PATHS.LANDING_PAGE);
    } else if (!onboardingPrerequisitesComplete) {
      history.replace(PATHS.ONBOARDING_UNPAID_INVITE_PROFILE);
    }
  }, [
    history,
    onboardingPrerequisitesComplete,
    requiresPayment,
    waitingForActivation
  ]);

  useEffect(() => {
    if (!requiresPayment || !onboardingPrerequisitesComplete) {
      return;
    }

    getPaymentLink()
      .then((response) => {
        const link =
          response && "data" in response && response.data?.data?.ecommerceLink;
        if (link) {
          setEcommerceLink(link);
        } else {
          handleLoadFailure();
        }
      })
      .catch(() => {
        handleLoadFailure();
      });
  }, [handleLoadFailure, onboardingPrerequisitesComplete, requiresPayment]);

  useEffect(() => {
    globalThis.scrollTo(0, 0);
  }, []);

  PageTitle("Payment details");

  if (
    (!requiresPayment || !onboardingPrerequisitesComplete) &&
    !waitingForActivation
  ) {
    return null;
  }

  if (waitingForActivation) {
    return (
      <GradientFramedLayout showHeader={false}>
        <Box
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            marginX: "auto",
            maxWidth: 1120,
            minHeight: { md: "min(70dvh, calc(100dvh - 6rem))", xs: "min(62dvh, calc(100dvh - 6.5rem))" },
            px: { md: 3, xs: 2 },
            py: { md: 4, xs: 3 },
            width: "100%"
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: COLORS.WHITE,
              borderRadius: 2,
              boxShadow: "0 16px 40px rgba(17, 24, 39, 0.08)",
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              justifyContent: "center",
              maxWidth: 520,
              px: { md: 5, xs: 3 },
              py: { md: 5, xs: 4 },
              textAlign: "center",
              width: "100%"
            }}
          >
            <LoadingAnimation />
            <Typography
              component="h1"
              sx={{
                color: COLORS.BLACK,
                fontSize: { md: "1.25rem", xs: "1.125rem" },
                fontWeight: 700,
                lineHeight: 1.45,
                m: 0
              }}
            >
              Please wait, we are activating your membership...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: alpha(COLORS.DARK_GRAY_2, 0.95),
                lineHeight: 1.6,
                m: 0,
                maxWidth: 420
              }}
            >
              {showLongWaitCopy
                ? "Almost there, please bear with us..."
                : "To avoid issues, do not close this window or refresh this page."}
            </Typography>
          </Box>
        </Box>
      </GradientFramedLayout>
    );
  }

  return (
    <GradientFramedLayout showHeader={false}>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: { md: 3, xs: 2.5 },
          marginX: "auto",
          maxWidth: 1120,
          minWidth: 0,
          pb: { md: 8, xs: 6 },
          pt: { md: 2, xs: 1.5 },
          px: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <UnpaidInviteRegistrationStepper activeStepIndex={2} />

        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: { lg: 3, xs: 2.5 },
            width: "100%"
          }}
        >
          <Box
            sx={{
              flex: { lg: "1 1 0%", xs: "none" },
              minWidth: 0,
              order: { lg: 0, xs: 1 },
              width: "100%"
            }}
          >
            <Box
              sx={{
                backgroundColor: COLORS.WHITE,
                borderRadius: 2,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                gap: 0,
                minWidth: 0,
                p: { md: "30px", xs: "24px 16px" },
                width: "100%"
              }}
            >
              <Typography
                component="h1"
                sx={{ typography: { xs: "h5_black", md: "h4_black" }, m: 0 }}
              >
                Payment details
              </Typography>
              <Divider
                sx={{
                  borderColor: alpha(COLORS.DARK_GRAY, 0.25),
                  mb: 2,
                  mt: 2,
                  width: "100%"
                }}
              />
              <Typography
                component="p"
                sx={{
                  color: COLORS.DARK_GRAY,
                  m: 0,
                  mb: 2,
                  textAlign: "center",
                  typography: "body_sm_dark_gray"
                }}
              >
                {formattedInitialPayment}
              </Typography>

              {!ecommerceLink ? (
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    minHeight: 280,
                    width: "100%"
                  }}
                >
                  <LoadingAnimation />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      position: "relative",
                      boxSizing: "border-box",
                      maxHeight: iframeLoaded
                        ? outerMaxHeightLoaded
                        : LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX,
                      maxWidth: "100%",
                      minHeight: iframeLoaded
                        ? outerMinHeightLoaded
                        : outerMinHeightLoading,
                      minWidth: 0,
                      mx: "auto",
                      overflow: iframeLoaded ? "visible" : "hidden",
                      pb: { xs: 1, md: 2 },
                      pt: 0,
                      px: 0,
                      transition: (theme) =>
                        theme.transitions.create(["max-height", "min-height"], {
                          duration: 450,
                          easing: theme.transitions.easing.easeInOut
                        }),
                      width: "100%"
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: COLORS.WHITE,
                        borderRadius: `${IFRAME_ROUNDED_CORNER_RADIUS_PX}px`,
                        boxSizing: "border-box",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                        p: `${IFRAME_ROUNDED_CORNER_PADDING_PX}px`,
                        width: "100%"
                      }}
                    >
                      <iframe
                        ref={paymentIframeRef}
                        key={ecommerceLink}
                        title="Membership payment"
                        src={ecommerceLink}
                        onError={handlePaymentEmbedFailed}
                        onLoad={() => setIframeLoaded(true)}
                        style={{
                          border: "none",
                          display: "block",
                          marginLeft: "auto",
                          marginRight: "auto",
                          maxWidth: "100%",
                          minHeight: iframeLoaded
                            ? iframeMinHeightLoaded
                            : LOADING_VIEWPORT_MAX_PX,
                          minWidth: 0,
                          opacity: iframeLoaded ? 1 : 0,
                          transition:
                            "min-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in",
                          width: "100%"
                        }}
                      />
                    </Box>
                    {!iframeLoaded && (
                      <Box
                        sx={{
                          alignItems: "center",
                          bgcolor: "transparent",
                          display: "flex",
                          inset: 0,
                          justifyContent: "center",
                          p: { md: 3, xs: 2 },
                          position: "absolute"
                        }}
                      >
                        <LoadingAnimation />
                      </Box>
                    )}
                  </Box>
                  <Box
                    component={RouterLink}
                    sx={{
                      color: COLORS.PRIMARY,
                      display: "block",
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      mt: 2,
                      textAlign: "center",
                      textDecoration: "none",
                      "&:hover": {
                        color: COLORS.PRIMARY_DARK,
                        textDecoration: "underline"
                      }
                    }}
                    to={PATHS.ONBOARDING_UNPAID_INVITE_PROFILE}
                  >
                    or Cancel payment
                  </Box>
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flex: { lg: "0 0 auto", xs: "none" },
              order: { lg: 1, xs: 0 },
              position: { lg: "sticky", xs: "static" },
              top: { lg: 16, xs: "auto" },
              width: { lg: "auto", xs: "100%" }
            }}
          >
            <UnpaidInviteCompleteSummaryCard />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default UnpaidInvitePaymentPage;
